import React, { useState, Context, useContext } from 'react'
import {
  EmployeeReportQueryParams,
  CreateEmployeeReport,
  ReportQueryParamKeys,
  EmployeeReport,
  EmployeeReportQueryResult,
  ChangeMultipReportStatusVariables,
  DeleteMultipleReportsVariables
} from 'models'
import { useQuery, useMutation } from '@apollo/client'
import {
  CREATE_EMPLOYEE_REPORT,
  UPDATE_EMPLOYEE_REPORT,
  DELETE_EMPLOYEE_REPORT,
  QUERY_EMPLOYEE_REPORTS,
  DELETE_MULTIPLE_REPORTS,
  CHANGE_MULTIPLE_REPORTS_STATUS
} from './graphql'
import Notification from 'antd/es/notification'

export type State = {
  data: EmployeeReportQueryResult | undefined

  queryParams: EmployeeReportQueryParams
  setQueryParam: (values: { key: ReportQueryParamKeys; value: any }[]) => void
  setQueryParams: (queryParams: EmployeeReportQueryParams) => void
  setDefaultQueryParams: () => void

  loading: boolean
  refetch: () => void

  createEmployeeReport: (createReport: CreateEmployeeReport) => Promise<EmployeeReport>
  deleteEmployeeReport: (params: EmployeeReport) => Promise<boolean>
  updateEmployeeReport: (updateReport: EmployeeReport) => Promise<EmployeeReport>
  queryEmployeeReports: (params: EmployeeReportQueryParams) => Promise<EmployeeReportQueryResult>
  deleteMultipleEmployeeReports: (params: DeleteMultipleReportsVariables) => Promise<boolean>
  changeMultipleEmployeeReportsStatus: (params: ChangeMultipReportStatusVariables) => Promise<boolean>
}

const shadowContext = React.createContext<State | undefined>(undefined)

const defaultQueryParams: EmployeeReportQueryParams = {
  page: undefined,
  customer_id: undefined,
  assignment_id: undefined,
  employee_id: undefined,
  status: undefined,
  start_time_iso_date: undefined,
  end_time_iso_date: undefined
}

export const context = shadowContext as Context<State>

export const useEmployeeReporting = () => useContext(context)

export const EmployeeReportingProvider = ({ children }: { children?: React.ReactNode }) => {
  const [_createMutation] = useMutation<{ createEmployeeReport: EmployeeReport }, { employee_report: CreateEmployeeReport }>(CREATE_EMPLOYEE_REPORT)
  const [_deleteMutation] = useMutation<{ deleteEmployeeReport: boolean }, { _id: string }>(DELETE_EMPLOYEE_REPORT)
  const [_deleteMultipleMutation] = useMutation<{ deleteMultipleEmployeeReports: boolean }, DeleteMultipleReportsVariables>(DELETE_MULTIPLE_REPORTS)

  const [_changeMultipleStatus] = useMutation<{ changeMultipleEmployeeReportStatus: boolean }, ChangeMultipReportStatusVariables>(
    CHANGE_MULTIPLE_REPORTS_STATUS
  )
  const [_updateMutation] = useMutation<{ updateEmployeeReport: EmployeeReport }, { employee_report: EmployeeReport }>(UPDATE_EMPLOYEE_REPORT)

  const getDefaultQueryParams = (): EmployeeReportQueryParams => {
    try {
      const defaultFromLocalstorage = localStorage.getItem('aputeamReportsQueryParams') as any
      if (defaultFromLocalstorage) {
        return JSON.parse(defaultFromLocalstorage)
      } else {
        throw new Error('error')
      }
    } catch (e) {
      return defaultQueryParams
    }
  }

  const setDefaultQueryParams = () => {
    setQueryParams(defaultQueryParams)
  }

  const [queryParams, _setQueryParams] = useState<EmployeeReportQueryParams>(getDefaultQueryParams())

  const { loading, data, refetch } = useQuery<{ employeeReports: EmployeeReportQueryResult | undefined }>(QUERY_EMPLOYEE_REPORTS, {
    variables: { queryParams }
  })

  const setQueryParams = (queryParams: EmployeeReportQueryParams) => {
    localStorage.setItem('aputeamReportsQueryParams', JSON.stringify(queryParams))

    _setQueryParams(queryParams)
  }

  const setQueryParam = (values: { key: ReportQueryParamKeys; value: any }[]) => {
    const current: EmployeeReportQueryParams = { ...queryParams }

    values.forEach((item) => {
      if (item.value) {
        current[item.key] = item.value
      } else {
        delete current[item.key]
      }
    })

    setQueryParams(current)
  }

  // useEffect(() => {
  //   queryEmployeeReports(queryParams)
  // }, [queryParams])

  const queryEmployeeReports = async (params: EmployeeReportQueryParams): Promise<EmployeeReportQueryResult> => {
    try {
      const { errors, data: dataRes } = await refetch()
      if (!errors && dataRes.employeeReports) {
        return dataRes?.employeeReports
      } else {
        throw new Error(`Could not fetch`)
      }
    } catch (e) {
      //@ts-ignore
      Notification.error({ message: 'Ei voitu hakea reportteja', description: e.message, placement: 'bottomLeft' })
      throw e
    }
  }

  const createEmployeeReport = async (params: CreateEmployeeReport): Promise<EmployeeReport> => {
    try {
      const { data, errors } = await _createMutation({ variables: { employee_report: params } })
      if (!errors && data) {
        refetch()
        Notification.success({ message: 'Luotiin raportti', placement: 'bottomLeft' })
        return data?.createEmployeeReport
      } else {
        throw JSON.stringify(errors)
      }
    } catch (e) {
      //@ts-ignore
      Notification.error({ message: 'Ei voitu luoda raporttia', description: e.message, placement: 'bottomLeft' })
      throw e
    }
  }

  const updateEmployeeReport = async (params: EmployeeReport): Promise<EmployeeReport> => {
    try {
      const { data, errors } = await _updateMutation({ variables: { employee_report: params } })
      if (!errors && data) {
        Notification.success({ message: 'Päivitettiin raportti', placement: 'bottomLeft' })
        refetch()
        return data?.updateEmployeeReport
      } else {
        throw errors
      }
    } catch (e) {
      //@ts-ignore
      Notification.error({ message: 'Ei voitu päivittää raporttia', description: e.message, placement: 'bottomLeft' })
      throw e
    }
  }

  const deleteEmployeeReport = async (params: EmployeeReport): Promise<boolean> => {
    try {
      const { data, errors } = await _deleteMutation({ variables: { _id: params._id } })
      if ((!errors || (errors && errors?.length === 0)) && data) {
        refetch()
        Notification.success({ message: 'Poistettiin raportti', placement: 'bottomLeft' })
        return data?.deleteEmployeeReport
      } else {
        throw errors
      }
    } catch (e) {
      //@ts-ignore
      Notification.error({ message: 'Ei voitu poistaa raporttia', description: e.message, placement: 'bottomLeft' })
      throw e
    }
  }

  const deleteMultipleEmployeeReports = async (params: DeleteMultipleReportsVariables): Promise<boolean> => {
    try {
      const { data, errors } = await _deleteMultipleMutation({ variables: params })
      if (data?.deleteMultipleEmployeeReports) {
        refetch()
        Notification.success({ message: `Poistettiin ${params._ids.length} raportti`, placement: 'bottomLeft' })
        return data?.deleteMultipleEmployeeReports
      } else {
        throw errors
      }
    } catch (e) {
      //@ts-ignore
      Notification.error({ message: 'Ei voitu poistaa raportteja', description: e.message, placement: 'bottomLeft' })
      throw e
    }
  }

  const changeMultipleEmployeeReportsStatus = async (params: ChangeMultipReportStatusVariables): Promise<boolean> => {
    try {
      const { data, errors } = await _changeMultipleStatus({ variables: params })
      if (data?.changeMultipleEmployeeReportStatus) {
        refetch()
        Notification.success({ message: `Päivitettiin ${params._ids.length} raporttia`, placement: 'bottomLeft' })
        return data?.changeMultipleEmployeeReportStatus
      } else {
        throw errors
      }
    } catch (e) {
      //@ts-ignore
      Notification.error({ message: 'Ei voitu päivittää raportteja', description: e.message, placement: 'bottomLeft' })
      throw e
    }
  }

  const { Provider } = context

  return (
    <Provider
      value={{
        data: data?.employeeReports,
        refetch,

        queryParams,
        setQueryParams,
        setQueryParam,
        setDefaultQueryParams,

        loading,

        queryEmployeeReports,
        createEmployeeReport,
        updateEmployeeReport,
        deleteEmployeeReport,
        deleteMultipleEmployeeReports,
        changeMultipleEmployeeReportsStatus
      }}
    >
      {children}
    </Provider>
  )
}
